import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, MapPin, Briefcase } from "lucide-react";
import api from "@/lib/api";
import { Spinner, EmptyState, Pagination, DemoBadge } from "@/components/common";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const ALL = "__all__";

export default function Businesses() {
  const [params, setParams] = useSearchParams();
  const [industries, setIndustries] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(params.get("search") || "");
  const industry = params.get("industry") || "";
  const city = params.get("city") || "";
  const page = parseInt(params.get("page") || "1", 10);

  useEffect(() => {
    api.get("/meta").then(({ data }) => setIndustries(data.industries)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/businesses", {
        params: { search: search || undefined, industry: industry || undefined, city: city || undefined, page },
      });
      setData(data);
    } catch {
      setData({ items: [], total: 0, page: 1, pages: 1 });
    } finally {
      setLoading(false);
    }
  }, [search, industry, city, page]);

  useEffect(() => { load(); }, [load]);

  const update = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => { if (v) next.set(k, v); else next.delete(k); });
    if (!("page" in patch)) next.set("page", "1");
    setParams(next);
  };

  return (
    <div className="container-nx section-nx">
      <div className="label-eyebrow mb-2">Business Directory</div>
      <h1 className="heading-2">Explore community businesses</h1>
      <p className="mt-3 max-w-2xl text-base text-slate-500">
        Find manufacturers, traders, service providers and suppliers represented by community members.
      </p>

      <div className="mt-8 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && update({ search })}
            placeholder="Search businesses, products or services..."
            className="input-nx pl-10"
            data-testid="business-search-input"
          />
        </div>
        <input
          defaultValue={city}
          onKeyDown={(e) => e.key === "Enter" && update({ city: e.target.value })}
          onBlur={(e) => e.target.value !== city && update({ city: e.target.value })}
          placeholder="City"
          className="input-nx sm:w-40"
          data-testid="business-city-input"
        />
        <Select value={industry || ALL} onValueChange={(v) => update({ industry: v === ALL ? "" : v })}>
          <SelectTrigger className="input-nx h-auto sm:w-64" data-testid="business-industry-select">
            <SelectValue placeholder="All industries" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All industries</SelectItem>
            {industries.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
          </SelectContent>
        </Select>
        <button onClick={() => update({ search })} className="btn-navy" data-testid="business-search-btn">Search</button>
      </div>

      <div className="mt-8">
        {loading || !data ? (
          <Spinner label="Loading businesses..." />
        ) : data.items.length === 0 ? (
          <EmptyState title="No businesses found" message="Try adjusting your filters. Members can add their own listings from the dashboard." icon={Briefcase} />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {data.items.map((b) => (
                <Link key={b.id} to={`/businesses/${b.id}`} className="card-nx card-nx-hover overflow-hidden" data-testid={`business-card-${b.id}`}>
                  <div className="h-36 w-full overflow-hidden bg-[#F5F3EF]">
                    {b.logo_url ? (
                      <img src={b.logo_url} alt={b.name} className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full items-center justify-center"><Briefcase className="h-10 w-10 text-slate-300" /></div>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2">
                      <h3 className="truncate font-semibold text-[#0B192C] font-head">{b.name}</h3>
                      {b.is_demo && <DemoBadge />}
                    </div>
                    <p className="mt-1 flex items-center gap-1 text-xs text-slate-400">
                      <MapPin className="h-3 w-3" /> {b.city}{b.service_area ? ` · ${b.service_area}` : ""}
                    </p>
                    <span className="chip-gold mt-3">{b.industry}</span>
                    {b.description && <p className="mt-3 line-clamp-2 text-sm text-slate-500">{b.description}</p>}
                  </div>
                </Link>
              ))}
            </div>
            <Pagination page={data.page} pages={data.pages} onChange={(p) => update({ page: String(p) })} />
          </>
        )}
      </div>
    </div>
  );
}
